import { MapboxGeoJSONFeature } from "mapbox-gl";

import { TreeItem, TreeItemIndex } from "ui/Tree";

import { commentGroupConfig, commentLayerConfigs } from "fond/layers";
import { Configuration } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { generateTreeItems } from "fond/utils/configurations";

import { FeaturesListItemType } from "./FeaturesList";

/**
 * Maps features to their layer/sublayer IDs based on style membership
 */
export function mapFeaturesByConfigId(
  features: MapboxGeoJSONFeature[],
  layerConfigs: Array<LayerConfig | SublayerConfig>
): Map<string, MapboxGeoJSONFeature[]> {
  const map = new Map<string, MapboxGeoJSONFeature[]>();

  // Include comment layer configs to ensure comment features can be matched
  const allLayerConfigs = [...layerConfigs, ...(Object.values(commentLayerConfigs.entities) as LayerConfig[])];

  features.forEach((feature) => {
    // Find the layer/sublayer config that contains this feature's style
    const layerConfig = allLayerConfigs.find((item) => item.Styles.includes(feature.layer.id));

    if (layerConfig) {
      const existing = map.get(layerConfig.ID) || [];
      map.set(layerConfig.ID, [...existing, feature]);
    }
  });

  return map;
}

/**
 * Builds tree items from configuration and augments with individual features.
 *
 * This function combines generateTreeItems() with feature augmentation, creating a complete
 * tree structure with config hierarchy (groups → layers → sublayers) plus individual features.
 *
 * @param config - Project configuration containing the layer structure
 * @param features - Array of map features to add to the tree
 * @param layerConfigs - Layer configurations to handle comment layers not in config
 */
export function buildFullTreeItems(
  config: Configuration,
  features: MapboxGeoJSONFeature[],
  layerConfigs: Array<LayerConfig | SublayerConfig>
): Record<TreeItemIndex, TreeItem<FeaturesListItemType>> {
  // Generate base tree structure from config
  const baseTreeItems = generateTreeItems({ config, showStyles: false });

  // Map features to their layer/sublayer config IDs
  const featuresByConfigId = mapFeaturesByConfigId(features, layerConfigs);

  const augmented: Record<TreeItemIndex, TreeItem<FeaturesListItemType>> = {};

  // Copy all base tree items and add feature children
  Object.entries(baseTreeItems).forEach(([key, item]) => {
    const itemFeatures = featuresByConfigId.get(key) || [];

    // Add feature IDs as children for layers/sublayers that have features
    const featureChildren = itemFeatures.map((feature, index) => `${key}-feature-${index}`);

    augmented[key] = {
      ...item,
      children: [...(item.children || []), ...featureChildren],
      isFolder: (item.children && item.children.length > 0) || featureChildren.length > 0,
    };

    // Add individual feature tree items
    itemFeatures.forEach((feature, index) => {
      const featureId = `${key}-feature-${index}`;
      augmented[featureId] = {
        index: featureId,
        canMove: false,
        children: [],
        isFolder: false,
        data: {
          Type: "Feature",
          feature,
        },
      };
    });
  });

  // Handle layer configs that have features but aren't in baseTreeItems (e.g., comment layers)
  const allLayerConfigs = [...layerConfigs, ...(Object.values(commentLayerConfigs.entities) as LayerConfig[])];
  const commentGroupChildren: string[] = [];

  featuresByConfigId.forEach((itemFeatures, layerConfigId) => {
    // Skip if already processed in baseTreeItems
    if (augmented[layerConfigId]) return;

    // Find the layer config
    const layerConfig = allLayerConfigs.find((cfg) => cfg.ID === layerConfigId);
    if (!layerConfig) return;

    // Create feature children
    const featureChildren = itemFeatures.map((feature, index) => `${layerConfigId}-feature-${index}`);

    // Create the layer tree item
    augmented[layerConfigId] = {
      index: layerConfigId,
      canMove: false,
      children: featureChildren,
      isFolder: true,
      data: layerConfig,
    };

    // Add to comment group children
    commentGroupChildren.push(layerConfigId);

    // Add individual feature tree items
    itemFeatures.forEach((feature, index) => {
      const featureId = `${layerConfigId}-feature-${index}`;
      augmented[featureId] = {
        index: featureId,
        canMove: false,
        children: [],
        isFolder: false,
        data: {
          Type: "Feature",
          feature,
        },
      };
    });
  });

  // Create Project Comments group if there are comment layers with features
  if (commentGroupChildren.length > 0) {
    const commentsGroupId = commentGroupConfig.ID;
    augmented[commentsGroupId] = {
      index: commentsGroupId,
      canMove: false,
      children: commentGroupChildren,
      isFolder: true,
      data: {
        ...commentGroupConfig,
        Children: commentGroupChildren,
      },
    };

    // Add to root children at the top
    if (augmented.root) {
      augmented.root = {
        ...augmented.root,
        children: [commentsGroupId, ...(augmented.root.children || [])],
      };
    }
  }

  return augmented;
}

/**
 * Checks if a tree item or any of its descendants contain features
 */
function hasFeatures(itemId: TreeItemIndex, treeItems: Record<TreeItemIndex, TreeItem<FeaturesListItemType>>): boolean {
  const item = treeItems[itemId];
  if (!item) return false;

  // Feature items always count as having features
  if (item.data?.Type === "Feature") return true;

  // Check if any children have features (recursively)
  if (item.children && item.children.length > 0) {
    return item.children.some((childId) => hasFeatures(childId, treeItems));
  }

  return false;
}

/**
 * Filters tree items to only include nodes that have features or have children with features
 */
export function filterTreeItemsByFeatures(
  fullTreeItems: Record<TreeItemIndex, TreeItem<FeaturesListItemType>>
): Record<TreeItemIndex, TreeItem<FeaturesListItemType>> {
  const filterItem = (itemId: TreeItemIndex): TreeItem<FeaturesListItemType> | null => {
    const item = fullTreeItems[itemId];
    if (!item) return null;

    // If this item or its children don't have features, exclude it
    if (!hasFeatures(itemId, fullTreeItems)) return null;

    // Filter children recursively
    const filteredChildren = (item.children || [])
      .map((childId) => filterItem(childId))
      .filter((child): child is TreeItem<FeaturesListItemType> => child !== null)
      .map((child) => child.index);

    return {
      ...item,
      children: filteredChildren,
      isFolder: filteredChildren.length > 0,
    };
  };

  // Start from root
  const rootItem = filterItem("root");
  if (!rootItem) return {};

  // Build the filtered tree
  const filtered: Record<TreeItemIndex, TreeItem<FeaturesListItemType>> = {
    root: rootItem,
  };

  const addToFiltered = (itemId: TreeItemIndex) => {
    const item = fullTreeItems[itemId];
    if (!item || filtered[itemId]) return;

    if (hasFeatures(itemId, fullTreeItems)) {
      const filteredChildren = (item.children || []).filter((childId) => hasFeatures(childId, fullTreeItems));
      filtered[itemId] = {
        ...item,
        children: filteredChildren,
        isFolder: filteredChildren.length > 0,
      };

      filteredChildren.forEach((childId) => addToFiltered(childId));
    }
  };

  if (rootItem.children) {
    rootItem.children.forEach((childId) => addToFiltered(childId));
  }

  return filtered;
}

/**
 * Helper to check if an item has any feature children (recursively)
 */
function hasFeatureChildren(itemId: TreeItemIndex, treeItems: Record<TreeItemIndex, TreeItem<FeaturesListItemType>>): boolean {
  const item = treeItems[itemId];
  if (!item) return false;

  if (item.data?.Type === "Feature") return true;

  return (item.children || []).some((childId) => hasFeatureChildren(childId, treeItems));
}

/**
 * Checks if an item name matches the search text
 */
function itemNameMatches(itemId: TreeItemIndex, treeItems: Record<TreeItemIndex, TreeItem<FeaturesListItemType>>, search: string): boolean {
  const item = treeItems[itemId];
  if (!item || !item.data) return false;

  let title = "";
  if (item.data.Type === "STYLE") {
    title = item.data.Name;
  } else if ("Label" in item.data) {
    title = item.data.Label;
  }

  return title.toLowerCase().includes(search) && hasFeatureChildren(itemId, treeItems);
}

/**
 * Checks if a tree item matches the search criteria
 */
function matchesSearch(
  itemId: TreeItemIndex,
  treeItems: Record<TreeItemIndex, TreeItem<FeaturesListItemType>>,
  search: string,
  getComment: (commentId: string) => any
): boolean {
  const item = treeItems[itemId];
  if (!item) return false;

  // Handle feature items - use substring matching
  if (item.data?.Type === "Feature") {
    const { feature } = item.data;
    const fondId = feature.properties?.FondID || feature.properties?.BiarriID || "";
    if (fondId.toLowerCase().includes(search)) return true;

    // Also check comment creator email for comment features
    if (feature.properties?.commentID) {
      const comment = getComment(feature.properties.commentID);
      const email = comment?.Creator?.Email || "";
      return email.toLowerCase().includes(search);
    }

    return false;
  }

  // For config items (layers, groups, sublayers):
  // Check if the name matches AND it has feature children
  let title = "";
  if (item.data) {
    if (item.data.Type === "STYLE") {
      title = item.data.Name;
    } else if ("Label" in item.data) {
      title = item.data.Label;
    }
  }

  // If the layer/group name matches and it has features, include all its children
  if (title.toLowerCase().includes(search) && hasFeatureChildren(itemId, treeItems)) {
    return true;
  }

  // Otherwise, only include if it has matching children
  return (item.children || []).some((childId) => matchesSearch(childId, treeItems, search, getComment));
}

/**
 * Applies search filter to tree items
 */
export function filterTreeItemsBySearch(
  filteredTreeItems: Record<TreeItemIndex, TreeItem<FeaturesListItemType>>,
  searchText: string,
  getComment: (commentId: string) => any
): Record<TreeItemIndex, TreeItem<FeaturesListItemType>> {
  if (!searchText.trim()) return filteredTreeItems;

  const search = searchText.trim().toLowerCase();

  const filterBySearch = (itemId: TreeItemIndex, parentMatched: boolean = false): TreeItem<FeaturesListItemType> | null => {
    const item = filteredTreeItems[itemId];
    if (!item) return null;

    const thisItemMatches = itemNameMatches(itemId, filteredTreeItems, search);
    const shouldInclude = parentMatched || matchesSearch(itemId, filteredTreeItems, search, getComment);

    if (!shouldInclude) return null;

    // If this item or parent matched by name, include ALL children
    const includeAllChildren = parentMatched || thisItemMatches;

    const filteredChildren = (item.children || [])
      .map((childId) => filterBySearch(childId, includeAllChildren))
      .filter((child): child is TreeItem<FeaturesListItemType> => child !== null)
      .map((child) => child.index);

    return {
      ...item,
      children: filteredChildren,
      isFolder: filteredChildren.length > 0,
    };
  };

  const rootItem = filterBySearch("root", false);
  if (!rootItem) return {};

  const result: Record<TreeItemIndex, TreeItem<FeaturesListItemType>> = {
    root: rootItem,
  };

  const addToResult = (itemId: TreeItemIndex, parentMatched: boolean = false) => {
    const item = filteredTreeItems[itemId];
    if (!item || result[itemId]) return;

    const thisItemMatches = itemNameMatches(itemId, filteredTreeItems, search);
    const shouldInclude = parentMatched || matchesSearch(itemId, filteredTreeItems, search, getComment);

    if (shouldInclude) {
      const includeAllChildren = parentMatched || thisItemMatches;
      const filteredChildren = (item.children || []).filter((childId) => {
        const childItem = filteredTreeItems[childId];
        return childItem && (includeAllChildren || matchesSearch(childId, filteredTreeItems, search, getComment));
      });

      result[itemId] = {
        ...item,
        children: filteredChildren,
        isFolder: filteredChildren.length > 0,
      };

      filteredChildren.forEach((childId) => addToResult(childId, includeAllChildren));
    }
  };

  if (rootItem.children) {
    rootItem.children.forEach((childId) => addToResult(childId, false));
  }

  return result;
}

/**
 * Counts the number of features in a tree
 */
export function countFeatures(treeItems: Record<TreeItemIndex, TreeItem<FeaturesListItemType>>): number {
  let count = 0;
  Object.values(treeItems).forEach((item) => {
    if (item.data?.Type === "Feature") {
      count += 1;
    }
  });
  return count;
}
